import React, { useCallback, useId, useRef, useState } from 'react'
import { IconButton } from './icon-button'
import { MenuPanel, type FocusTarget } from './menu-panel'
import type { PopoverAlign } from './use-popover-placement'

/**
 * 動作選單（design-guideline §7.7）：右上「…」、右鍵、帳戶列都開同一種面板。
 * 選單內容是 `MenuItem`／`MenuItemRadio`／`MenuGroup`／`MenuSeparator`；入口有三個：
 * `MenuButton`（「…」圖示鈕）、`Menu`（自訂觸發元素，例如帳戶列）、`ContextMenu`
 * （右鍵，配 `useContextMenu`）。
 *
 * 面板跟 Dropdown、Picker 一樣在原地以 `position: fixed` 渲染，不 portal：選單可能開在
 * `KiaoModal` 的 `<dialog>` 裡，portal 到 body 會落在 top layer 底下變成 inert。
 * 一般選單寬 `--measure-menu`；`wide` 是帳戶選單那種兩行的寬版。
 */

export interface MenuTriggerProps {
  ref: React.Ref<HTMLButtonElement>
  'aria-haspopup': 'menu'
  'aria-expanded': boolean
  'aria-controls'?: string
  onClick: () => void
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
}

export interface MenuProps {
  /** The menu's accessible name. */
  label: string
  /** Renders the opener; spread the props onto a button. */
  trigger: (props: MenuTriggerProps) => React.ReactElement
  children: React.ReactNode
  /** Controlled open state, so something else can open it (「切換工作區」). */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Which trigger edge the panel lines up with. Default `start`. */
  align?: PopoverAlign
  /** The two-line account-menu panel. */
  wide?: boolean
}

export function Menu({
  label,
  trigger,
  children,
  open: controlledOpen,
  onOpenChange,
  align = 'start',
  wide = false,
}: MenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [focusTarget, setFocusTarget] = useState<FocusTarget>('first')
  const open = controlledOpen ?? uncontrolledOpen
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()

  const setOpen = useCallback(
    (next: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [controlledOpen, onOpenChange]
  )

  const openAt = (target: FocusTarget) => {
    setFocusTarget(target)
    setOpen(true)
  }

  const close = useCallback(
    (refocus: boolean) => {
      setOpen(false)
      if (refocus) triggerRef.current?.focus()
    },
    [setOpen]
  )

  return (
    <>
      {trigger({
        ref: triggerRef,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? panelId : undefined,
        onClick: () => (open ? close(false) : openAt('first')),
        onKeyDown: (event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
          event.preventDefault()
          openAt(event.key === 'ArrowUp' ? 'last' : 'first')
        },
      })}
      {open ? (
        <MenuPanel
          id={panelId}
          label={label}
          wide={wide}
          anchor={triggerRef}
          align={align}
          focusTarget={focusTarget}
          insideRefs={[triggerRef]}
          onClose={close}
        >
          {children}
        </MenuPanel>
      ) : null}
    </>
  )
}

export interface MenuButtonProps {
  /** The trigger's accessible name, and the menu's. */
  label: string
  icon: React.ReactNode
  children: React.ReactNode
  disabled?: boolean
}

/** The「…」entry: a bar icon button whose menu lines up with its right edge. */
export function MenuButton({ label, icon, children, disabled }: MenuButtonProps) {
  return (
    <Menu
      label={label}
      align="end"
      trigger={(props) => (
        <IconButton {...props} variant="bar" aria-label={label} title={label} disabled={disabled}>
          {icon}
        </IconButton>
      )}
    >
      {children}
    </Menu>
  )
}
